import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { FormField } from '../../../components/ui/FormField.js';
import { FormSelect } from '../../../components/ui/FormSelect.js';
import { OrgUnitPicker } from '../../org/components/OrgUnitPicker.js';
import { useOrgUnits } from '../../org/network/org.api.js';
import { useRoles } from '../../staff/network/staff.api.js';
import { agentHooks } from '../hooks.js';
import { useToast } from '../../../components/ui/Toast.js';
import type { EmploymentType } from '../types.js';
import { SchemaForm, type FieldConfigItem } from '../../../components/ui/SchemaForm.js';
import { CreateSchema as agentSchema, type AgentCreateInput, type AgentResponse } from '@repo/shared-agents/zod';
import {
  DynamicContactList,
  DynamicAddressList,
  DynamicBankList,
  type ContactItem,
  type AddressItem,
  type BankItem,
} from '@repo/web-form';

interface AgentFormProps {
  initialValues?: {
    fullName: string;
    mobile: string;
    email?: string | null;
    employmentType: EmploymentType;
    joinedAt: string | Date;
    supervisorId?: string | null;
    orgUnitId: string;
    roleId?: string;
  };
  agentId?: string;
}

const defaultValues = {
  fullName: '',
  mobile: '',
  email: '',
  employmentType: 'FULL_TIME' as EmploymentType,
  joinedAt: new Date().toISOString().split('T')[0],
  supervisorId: '',
  orgUnitId: '',
  roleId: '',
};

export function AgentForm({ initialValues, agentId }: AgentFormProps) {
  const navigate = useNavigate();
  const createAgent = agentHooks.useCreate();
  const updateAgent = agentHooks.useUpdate();
  const { toast } = useToast();

  const { data: orgUnitsResponse } = useOrgUnits();
  const { data: agentsResponse } = agentHooks.useList({ limit: 1000 });
  const { data: roles } = useRoles();
  const roleOptions = useMemo(() => {
    if (!roles) return [];
    return roles.map((r) => ({ value: r.id, label: r.name }));
  }, [roles]);

  const isEdit = !!agentId;
  const mutation = isEdit ? updateAgent : createAgent;

  // Dynamic 1-to-many relationship states
  const [contacts, setContacts] = useState<ContactItem[]>([
    { type: 'PHONE', label: 'MOBILE', value: initialValues?.mobile || '', isPrimary: true, isWhatsapp: true },
    ...(initialValues?.email ? [{ type: 'EMAIL' as const, label: 'WORK', value: initialValues.email, isPrimary: true }] : []),
  ]);
  const [addresses, setAddresses] = useState<AddressItem[]>([]);
  const [banks, setBanks] = useState<BankItem[]>([]);

  // Coerce joinedAt to string
  let joinedAtStr = '';
  if (initialValues?.joinedAt) {
    const d = initialValues.joinedAt;
    joinedAtStr = typeof d === 'string' ? d.split('T')[0] : d.toISOString().split('T')[0];
  }

  const initialFormValues = {
    ...defaultValues,
    ...(initialValues
      ? {
          ...initialValues,
          email: initialValues.email || '',
          supervisorId: initialValues.supervisorId || '',
          joinedAt: joinedAtStr || defaultValues.joinedAt,
        }
      : {}),
  };

  const fieldConfig: Partial<Record<keyof AgentCreateInput, FieldConfigItem>> = {
    fullName: {
      label: 'Full Name',
      placeholder: 'E.g. Jane Doe',
      order: 1,
    },
    mobile: {
      label: 'Primary Mobile',
      placeholder: 'E.g. 9876543210',
      order: 2,
    },
    email: {
      label: 'Primary Email',
      placeholder: 'E.g. jane@example.com',
      order: 3,
    },
    employmentType: {
      label: 'Employment Type',
      order: 4,
      options: [
        { value: 'FULL_TIME', label: 'Full Time' },
        { value: 'PART_TIME', label: 'Part Time' },
        { value: 'CONTRACT', label: 'Contract' },
      ],
    },
    joinedAt: {
      label: 'Joined Date',
      widget: 'date' as const,
      order: 5,
    },
    orgUnitId: {
      label: 'Primary Branch',
      order: 6,
      render: (field, isSubmitting, err) => (
        <FormField label="Primary Branch" htmlFor={field.name} error={err} required>
          <OrgUnitPicker
            nodes={(orgUnitsResponse as never) || []}
            value={field.state.value}
            onChange={field.handleChange}
            placeholder="Select primary branch"
          />
        </FormField>
      ),
    },
    roleId: {
      label: 'Role',
      order: 7,
      render: (field, isSubmitting, err) => (
        <FormSelect
          label="Role"
          required
          id={field.name}
          value={field.state.value || ''}
          onChange={(e) => field.handleChange(e.target.value)}
          disabled={isSubmitting}
          error={err}
          options={[{ value: '', label: 'Select role...' }, ...roleOptions]}
        />
      ),
    },
    supervisorId: {
      label: 'Supervisor (Optional)',
      order: 8,
      render: (field, isSubmitting, err) => (
        <FormSelect
          label="Supervisor (Optional)"
          id={field.name}
          value={field.state.value || ''}
          onChange={(e) => field.handleChange(e.target.value)}
          disabled={isSubmitting}
          error={err}
          options={[
            { value: '', label: 'None' },
            ...(agentsResponse?.data?.filter((a: AgentResponse) => a.status === 'ACTIVE' && a.id !== agentId).map((a: AgentResponse) => ({
              value: a.id,
              label: `${a.fullName} (${a.agentNumber})`,
            })) || []),
          ]}
        />
      ),
    },
  };

  return (
    <div className="space-y-8">
      <SchemaForm
        schema={agentSchema}
        defaultValues={initialFormValues}
        fieldConfig={fieldConfig}
        mutation={mutation}
        routeId={isEdit ? undefined : 'agents/new'}
        enableDraft={!isEdit}
        submitLabel="Save Agent Profile"
        onCancel={() => void navigate({ to: '/agents' })}
        onBeforeSubmit={(value) => {
          const primaryPhone = contacts.find((c) => c.type === 'PHONE' && c.isPrimary)?.value || contacts.find((c) => c.type === 'PHONE')?.value || value.mobile;
          const primaryEmail = contacts.find((c) => c.type === 'EMAIL' && c.isPrimary)?.value || contacts.find((c) => c.type === 'EMAIL')?.value || value.email || null;
          return {
            ...value,
            mobile: primaryPhone,
            email: primaryEmail,
            supervisorId: value.supervisorId || null,
            contacts,
            addresses,
            banks,
          };
        }}
        onSuccess={() => {
          void navigate({ to: '/agents' });
        }}
        onError={(err: unknown) => {
          toast({
            type: 'error',
            title: 'Error',
            description: err instanceof Error ? err.message : 'Failed to save agent',
          });
        }}
      />

      <div className="max-w-2xl border-t pt-6 space-y-6">
        <DynamicContactList
          contacts={contacts}
          onChange={(newContacts) => setContacts(newContacts)}
        />
        <DynamicAddressList
          addresses={addresses}
          onChange={(newAddresses) => setAddresses(newAddresses)}
        />
        <DynamicBankList
          banks={banks}
          onChange={(newBanks) => setBanks(newBanks)}
        />
      </div>
    </div>
  );
}

import { EmptyState } from '../../../components/ui/EmptyState.js';
export const _agentsEmpty = () => <EmptyState title="No data" description="No data found." icon={<span />} />;
